import { useContext } from 'react'
import { useRouter } from 'next/router'
import Image from 'next/legacy/image'

import { StateContext } from '~~/src/context/context'

import { TNetworkNames } from '~~/src/models/constants/networks'
import { ethereumChainId, ethereumNetwork, maticChainId, maticNetwork } from '~~/src/context/app.config'
import { EtherLogo, MaticLogo, hardhatLogo } from "~~/src/components/Main/utils/Assets"
import { useAccount, useNetwork, useSwitchNetwork } from 'wagmi'
import { toast } from 'react-hot-toast'
import { Box, LinearProgress, Stack } from '@mui/material'

const style = {
  container: `w-32 h-40 border border-slate-500 rounded-md hover:scale-105 transation-all duration-200 ease-out`
}

export const listStyle = {
  title: `text-xl sm:text-4xl dark:text-emerald-200 text-emerald-800 font-semibold`,
  header: `text-slate-800 dark:text-slate-300 font-medium text-xl sm:text-3xl`,
  switch: `text-slate-800 dark:text-slate-300 font-medium text-lg`,
  moment: `text-slate-800 dark:text-slate-300 font-medium text-lg`,
  selectType: `border rounded-lg h-20 w-32 font-semiblod grid justify-items-center content-center cursor-pointer transition-all duration-200 `,
  selectedType: `border-blue-800 text-blue-800 dark:border-blue-200 dark:text-blue-200`,
  unSelected: `dark:border-[#6c7983] border-emrald-800 dark:text-[#6c7983] text-[#b2becd] hover:text-[#454e56] dark:hover:border-[#b2becd] hover:border-[#454e56]`,
}

export type chainArrayType = {
  name: string,
  logo: JSX.Element,
  chainId: number,
  network: TNetworkNames,
  symbol: string
}

export const chainArray: Array<chainArrayType> =
  [
    {
      name: 'ethereum',
      logo: <Image src={EtherLogo} alt="ethereum" width={100} height={100} />,
      chainId: 1, // ethereumChainId,
      network: "mainnet", // ethereumNetwork,
      symbol: "ETH"
    },
    {
      name: 'polygon',
      logo: <Image src={MaticLogo} alt="matic" width={100} height={100} />,
      chainId: 137, //maticChainId,
      network: "matic",
      symbol: "MATIC"
    },
    {
      name: 'hardhat',
      logo: <Image src={hardhatLogo} alt="hardhat" width={100} height={100} />,
      chainId: 31337,
      network: "localhost",
      symbol: "GO"
    },
    {
      name: 'Sepolia',
      logo: <Image src={EtherLogo} alt="ethereum" width={100} height={100} />,
      chainId: 11155111,
      network: "sepolia",
      symbol: "ETH"
    },
    {
      name: 'Amoy',
      logo: <Image src={MaticLogo} alt="matic" width={100} height={100} />,
      chainId: 80002,
      network: "amoy",
      symbol: "POL"
    },
  ];

const TESTNET_IDS = [
  31337,    // Hardhat
  11155111, // Sepolia
  80002     // Amoy
]

const isDevelopment = process.env.NODE_ENV === 'development';

export default function SelectChain() {
  const { targetNetwork, setTargetNetwork } = useContext(StateContext)
  const { switchNetworkAsync, isLoading, isSuccess } = useSwitchNetwork()
  const { chain } = useNetwork()
  const { isConnected } = useAccount()
  const router = useRouter()

  /** @dev Set the target network selected by user. */
  const setCredentials = async (item: chainArrayType) => {
    setTargetNetwork({ ...targetNetwork, network: item.network, chainId: item.chainId })
    router.push(`/item/chain/${item.name}`)
  }

  /** @dev Handle selected network by user. */
  const handleSelect = async (item: chainArrayType) => {
    if (isConnected) {
      if (item.chainId != chain?.id) {
        try {
          const switchData = await switchNetworkAsync?.(item?.chainId).then(value => value)
          if (switchData?.id == item.chainId) {
            await setCredentials(item)
          }
        } catch (err) {
          toast.error("User rejected the network switch.")
        }
      } else {
        await setCredentials(item)
      }
    } else {
      toast('🔌 Please connect your wallet first before minting.')
    }
  }

  const option = (item: chainArrayType, id: number) => {
    return (

      <button disabled={isLoading || isSuccess} aria-disabled={isLoading || isSuccess} key={id}
        className={style.container} onClick={() => {
          handleSelect(item)
        }}>
        <div className='cursor-pointer'>
          <div className='pt-4 px-4'>
            {item.logo}
          </div>
          {(isLoading || isSuccess) ?
            <Box sx={{ width: '100%' }}>
              {isSuccess ?
                <h4 className={"text-slate-800 dark:text-slate-300 font-medium text-base"}>Wait a moment</h4> :
                <h4 className={listStyle.switch}>Switching...</h4>}
              <Stack className='mx-4'>
                <LinearProgress />
              </Stack>
            </Box> :
            <h4 className={listStyle.header}>{item.name.charAt(0).toUpperCase() + item.name.slice(1)}</h4>
          }
        </div>
      </button>
    )
  }

  const select = (
    <div className='grid content-center justify-center h-screen'>
      <h3 className={listStyle.title}>Choose the Perfect Blockchain:</h3>
      <h5 className={listStyle.header + `text-blue-600`}>Select the blockchain that best suits your requirements.</h5>
      <div className={`flex ${process.env.NODE_ENV === 'development' && "flex-col sm:flex-row"} space-y-8 space-x-8 pt-10 justify-center items-baseline`}>
        {chainArray?.filter((item) => {
          if (isDevelopment) return true;

          // If in production, only return items that are NOT in our testnet list
          return !TESTNET_IDS.includes(item.chainId);
        })
          .map((item, id) => {
            return option(item, id)
          })}
      </div>
    </div >
  )

  return select
}
